import React, { useState, useEffect } from 'react';

const AddMemberForm = ({ onSubmit, onClose, selectedNode, formMode = 'child', initialData, isRequestMode = false }) => {
  const [activeTab, setActiveTab] = useState(initialData ? 'edit' : formMode);
  
  const [formData, setFormData] = useState({
    name: '', gender: 'Male', birthOrder: '', spouseOrder: '', 
    birthDate: '', deathDate: '', marriageDate: '',  
    bio: '', phoneNumber: '', email: '', website: '',       
    spouseId: '', parentId: '', targetChildId: '' 
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setActiveTab('edit'); 
    } else {
      setActiveTab(formMode); 
      setFormData(prev => ({
        ...prev, name: '', gender: 'Male', birthOrder: '', spouseOrder: '',
        birthDate: '', deathDate: '', marriageDate: '',
        bio: '', phoneNumber: '', email: '', website: ''
      }));
    }
  }, [initialData, formMode, selectedNode]);

  useEffect(() => {
    if (!initialData && selectedNode && activeTab === 'spouse') {
        setFormData(prev => ({ ...prev, gender: selectedNode.gender === 'Male' ? 'Female' : 'Male' }));
    }
  }, [activeTab, selectedNode, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...formData };
    
    if (!initialData && selectedNode) {
        payload.targetMemberId = selectedNode.id;
        if (activeTab === 'child') {
            payload.parentId = selectedNode.id;
            if (selectedNode.gender === 'Male') payload.fatherId = selectedNode.id;
            else payload.motherId = selectedNode.id;
        } else if (activeTab === 'spouse') payload.spouseId = selectedNode.id;
        else if (activeTab === 'parent') payload.targetChildId = selectedNode.id;
    }

    payload.relationType = activeTab; 
    payload.relationshipType = activeTab; 
    onSubmit(payload);
  };

  const getTitle = () => {
      if (initialData) return "Edit Member Details";
      const action = isRequestMode ? "Request New" : "Add New";
      const type = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
      return `${action} ${type}`;
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fadeIn">
        
        <div className={`px-6 py-4 flex justify-between items-center ${isRequestMode ? 'bg-purple-600' : 'bg-blue-600'}`}>
          <div>
            <h2 className="text-xl font-bold text-white">{getTitle()}</h2>
            {selectedNode && !initialData && (
               <p className="text-blue-100 text-xs mt-1">Connected to: <span className="font-bold">{selectedNode.name}</span></p>
            )}
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white rounded-full p-2 transition">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {!initialData && selectedNode && (
             <div className="bg-slate-100 p-1.5 rounded-xl border border-slate-200">
                <div className="flex bg-white rounded-lg shadow-sm overflow-hidden divide-x divide-slate-100">
                    {['child', 'spouse', 'parent'].map(type => (
                        <button key={type} type="button" onClick={() => setActiveTab(type)} className={`flex-1 py-2.5 text-xs font-bold capitalize transition-all duration-200 ${activeTab === type ? (isRequestMode ? 'bg-purple-50 text-purple-700 shadow-inner' : 'bg-blue-50 text-blue-700 shadow-inner') : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}>{type}</button>
                    ))}
                </div>
             </div>
          )}

          <div className="grid grid-cols-2 gap-4">
             <div className="col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name</label>
                <input required type="text" name="name" value={formData.name || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none font-medium" />
             </div>

             <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Gender</label>
                <select name="gender" value={formData.gender || 'Male'} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Male">Male</option><option value="Female">Female</option>
                </select>
             </div>

             <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Birth Order</label>
                <input type="number" min="1" name="birthOrder" value={formData.birthOrder || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. 1 (Eldest)" />
             </div>

             {/* Spouse Order (Only visible to Admins adding a spouse) */}
             {!isRequestMode && activeTab === 'spouse' && (
                <div className="col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Spouse Order (For Multiple Spouses)</label>
                    <input type="number" min="1" name="spouseOrder" value={formData.spouseOrder || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. 1 (First Spouse)" />
                </div>
             )}
             
             {/* FLEXIBLE DATES WITH HYBRID CALENDAR ICON */}
             <div className="relative">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Birth Date / Year</label>
                <div className="flex items-center">
                    <input type="text" name="birthDate" value={formData.birthDate || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500" placeholder="YYYY or YYYY-MM-DD" />
                    <input type="date" onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))} className="absolute right-2 opacity-0 w-8 h-8 cursor-pointer z-10" title="Choose from calendar" />
                    <span className="absolute right-3 text-lg pointer-events-none">📅</span>
                </div>
             </div>

             <div className="relative">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Death Date / Year</label>
                <div className="flex items-center">
                    <input type="text" name="deathDate" value={formData.deathDate || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500" placeholder="YYYY or YYYY-MM-DD" />
                    <input type="date" onChange={(e) => setFormData(prev => ({ ...prev, deathDate: e.target.value }))} className="absolute right-2 opacity-0 w-8 h-8 cursor-pointer z-10" title="Choose from calendar" />
                    <span className="absolute right-3 text-lg pointer-events-none">📅</span>
                </div>
             </div>

             <div className="col-span-2 relative">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Marriage Date / Year</label>
                <div className="flex items-center">
                    <input type="text" name="marriageDate" value={formData.marriageDate || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500" placeholder="YYYY or YYYY-MM-DD" />
                    <input type="date" onChange={(e) => setFormData(prev => ({ ...prev, marriageDate: e.target.value }))} className="absolute right-2 opacity-0 w-8 h-8 cursor-pointer z-10" title="Choose from calendar" />
                    <span className="absolute right-3 text-lg pointer-events-none">📅</span>
                </div>
             </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Biography</label>
            <textarea name="bio" value={formData.bio || ''} onChange={handleChange} rows="4" className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none resize-y text-sm" placeholder="Write the family history..." />
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 dashed-border">
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                 <input type="text" name="phoneNumber" value={formData.phoneNumber || ''} onChange={handleChange} className="border border-slate-300 p-2.5 rounded-lg text-sm w-full outline-none focus:border-blue-500" placeholder="Phone Number" />
                 <input type="email" name="email" value={formData.email || ''} onChange={handleChange} className="border border-slate-300 p-2.5 rounded-lg text-sm w-full outline-none focus:border-blue-500" placeholder="Email Address" />
                 <input type="text" name="website" value={formData.website || ''} onChange={handleChange} className="border border-slate-300 p-2.5 rounded-lg text-sm w-full outline-none focus:border-blue-500 sm:col-span-2" placeholder="Personal Website (http://...)" />
             </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition">Cancel</button>
            <button type="submit" className={`flex-1 py-3 text-white font-bold rounded-xl shadow-lg transition active:scale-95 ${isRequestMode ? 'bg-purple-600' : 'bg-blue-600'}`}>
                {initialData ? 'Save Changes' : isRequestMode ? 'Send Request' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMemberForm;









// import React, { useState, useEffect } from 'react';

// const AddMemberForm = ({ onSubmit, onClose, selectedNode, formMode = 'child', initialData, isRequestMode = false }) => {
//   // Tabs: 'child', 'spouse', 'parent'. If editing, we just use 'edit' mode.
//   const [activeTab, setActiveTab] = useState(initialData ? 'edit' : formMode);
  
//   const [formData, setFormData] = useState({
//     name: '',
//     gender: 'Male',
//     birthOrder: '', // NEW
//     birthDate: '',
//     deathDate: '',     
//     marriageDate: '',  
//     bio: '',
//     phoneNumber: '',
//     email: '',
//     website: '',       
//     spouseId: '', 
//     parentId: '', 
//     targetChildId: '' 
//   });

//   // Load data if editing, or reset if adding
//   useEffect(() => {
//     if (initialData) {
//       setFormData(initialData);
//       setActiveTab('edit'); 
//     } else {
//       setActiveTab(formMode); 
//       setFormData(prev => ({
//         ...prev,
//         name: '',
//         gender: 'Male',
//         birthOrder: '', // NEW
//         birthDate: '',
//         deathDate: '',
//         marriageDate: '',
//         bio: '',
//         phoneNumber: '',
//         email: '',
//         website: ''
//       }));
//     }
//   }, [initialData, formMode, selectedNode]);

//   // --- NEW: AUTO-SELECT SPOUSE GENDER LOGIC ---
//   useEffect(() => {
//     if (!initialData && selectedNode && activeTab === 'spouse') {
//         // If adding a spouse, automatically select the opposite gender of the current node
//         setFormData(prev => ({
//             ...prev,
//             gender: selectedNode.gender === 'Male' ? 'Female' : 'Male'
//         }));
//     }
//   }, [activeTab, selectedNode, initialData]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
    
//     // Prepare payload
//     const payload = { ...formData };
    
//     // Linkage Logic (Only for New Members)
//     if (!initialData && selectedNode) {
//         // ALWAYS send targetMemberId as a safety fallback
//         payload.targetMemberId = selectedNode.id;

//         if (activeTab === 'child') {
//             // Primary Parent ID
//             payload.parentId = selectedNode.id;
            
//             // Gender specific IDs (Extra safety)
//             if (selectedNode.gender === 'Male') payload.fatherId = selectedNode.id;
//             else payload.motherId = selectedNode.id;

//         } else if (activeTab === 'spouse') {
//             payload.spouseId = selectedNode.id;
//         } else if (activeTab === 'parent') {
//             payload.targetChildId = selectedNode.id;
//         }
//     }

//     // Backend expects 'relationType'
//     payload.relationType = activeTab; 
//     payload.relationshipType = activeTab; 

//     onSubmit(payload);
//   };

//   // Title Logic
//   const getTitle = () => {
//       if (initialData) return "Edit Member Details";
//       const action = isRequestMode ? "Request New" : "Add New";
//       const type = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
//       return `${action} ${type}`;
//   };

//   return (
//     // Z-Index 1000 ensures it's on top of everything
//     <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
//       <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fadeIn">
        
//         {/* Header */}
//         <div className={`px-6 py-4 flex justify-between items-center ${isRequestMode ? 'bg-purple-600' : 'bg-blue-600'}`}>
//           <div>
//             <h2 className="text-xl font-bold text-white">{getTitle()}</h2>
//             {selectedNode && !initialData && (
//                <p className="text-blue-100 text-xs mt-1">
//                  Connected to: <span className="font-bold">{selectedNode.name}</span>
//                </p>
//             )}
//           </div>
//           <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition">
//             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
//           </button>
//         </div>

//         <form onSubmit={handleSubmit} className="p-6 space-y-5">
          
//           {/* RELATIONSHIP TABS */}
//           {!initialData && selectedNode && (
//              <div className="bg-slate-100 p-1.5 rounded-xl border border-slate-200">
//                 <div className="flex justify-between items-center mb-1 px-2">
//                     <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Relationship Type</span>
//                 </div>
//                 <div className="flex bg-white rounded-lg shadow-sm overflow-hidden divide-x divide-slate-100">
//                     {['child', 'spouse', 'parent'].map(type => (
//                         <button
//                             key={type}
//                             type="button"
//                             onClick={() => setActiveTab(type)}
//                             className={`flex-1 py-2.5 text-xs font-bold capitalize transition-all duration-200
//                                 ${activeTab === type 
//                                     ? (isRequestMode ? 'bg-purple-50 text-purple-700 shadow-inner' : 'bg-blue-50 text-blue-700 shadow-inner') 
//                                     : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
//                         >
//                             {type}
//                         </button>
//                     ))}
//                 </div>
//              </div>
//           )}

//           {/* Fields */}
//           <div className="grid grid-cols-2 gap-4">
//              <div className="col-span-2">
//                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name</label>
//                 <input required type="text" name="name" value={formData.name || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none font-medium" placeholder="e.g. Rahul Sharma" />
//              </div>

//              <div>
//                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Gender</label>
//                 <select name="gender" value={formData.gender || 'Male'} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-blue-500">
//                     <option value="Male">Male</option>
//                     <option value="Female">Female</option>
//                 </select>
//              </div>

//              {/* NEW: BIRTH ORDER FIELD */}
//              <div>
//                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Birth Order</label>
//                 <input type="number" min="1" name="birthOrder" value={formData.birthOrder || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. 1 (Eldest)" />
//              </div>
             
//              {/* DATE FIELDS */}
//              <div>
//                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Birth Date</label>
//                 <input type="date" name="birthDate" value={formData.birthDate || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-slate-600" />
//              </div>

//              <div>
//                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Death Date</label>
//                 <input type="date" name="deathDate" value={formData.deathDate || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-slate-600" />
//              </div>

//              <div className="col-span-2">
//                 <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Marriage Date</label>
//                 <input type="date" name="marriageDate" value={formData.marriageDate || ''} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-slate-600" />
//              </div>
//           </div>

//           <div>
//             <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Biography (Approx 800 words)</label>
//             <textarea name="bio" value={formData.bio || ''} onChange={handleChange} rows="4" className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none resize-y text-sm" placeholder="Write the family history..." />
//           </div>

//           {/* Contact Box */}
//           <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 dashed-border">
//              <div className="flex items-center gap-2 mb-3">
//                  <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[10px]">📞</div>
//                  <span className="text-xs font-bold text-slate-400 uppercase">Contact Information</span>
//              </div>
//              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//                  <input type="text" name="phoneNumber" value={formData.phoneNumber || ''} onChange={handleChange} className="border border-slate-300 p-2.5 rounded-lg text-sm w-full outline-none focus:border-blue-500" placeholder="Phone Number" />
//                  <input type="email" name="email" value={formData.email || ''} onChange={handleChange} className="border border-slate-300 p-2.5 rounded-lg text-sm w-full outline-none focus:border-blue-500" placeholder="Email Address" />
//                  <input type="text" name="website" value={formData.website || ''} onChange={handleChange} className="border border-slate-300 p-2.5 rounded-lg text-sm w-full outline-none focus:border-blue-500 sm:col-span-2" placeholder="Personal Website (http://...)" />
//              </div>
//           </div>

//           <div className="flex gap-3 pt-4 border-t border-slate-100">
//             <button type="button" onClick={onClose} className="flex-1 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition">
//                 Cancel
//             </button>
//             <button type="submit" className={`flex-1 py-3 text-white font-bold rounded-xl shadow-lg transition transform active:scale-95 ${isRequestMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
//                 {initialData ? 'Save Changes' : isRequestMode ? 'Send Request' : 'Add Member'}
//             </button>
//           </div>

//         </form>
//       </div>
//     </div>
//   );
// };

// export default AddMemberForm;









